import {
    BaseLayer,
    BaseLayerFactory_3857,
    BaseLayerType
} from "@esdm/map";

import { IBaseLayer } from "@esdm/map/dist/baselayers/interfaces/IBaseLayer";
import TileLayer from "ol/layer/Tile";
import TileWMS from 'ol/source/TileWMS';

import * as olProj from 'ol/proj';
import proj4 from "proj4";
import { register } from 'ol/proj/proj4';
import Projection from "ol/proj/Projection";
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import ImageLayer from "ol/layer/Image";
import ImageWMS from 'ol/source/ImageWMS';
import WMTS from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { get as getProjection } from "ol/proj";

export const PortsmouthBaseLayerTypes = {
    ...BaseLayerType,
    PortsmouthOS: 999,
    PortsmouthAerial: 1000,
    PortsmouthOSSixInch: 1102,
    PortsmouthOS25inch: 1101,
    PortsmouthOS1250: 1103
};

export class PortsmouthBaseMapping_3857 extends BaseLayerFactory_3857 {

    public GetBaseLayerByType(baseLayerType: BaseLayerType, bingApiKey: string, esdmOsmApiKey:string): IBaseLayer {
        if (baseLayerType === PortsmouthBaseLayerTypes.PortsmouthOS) {
            return this.GetPortsmouthOS();
        }

        if (baseLayerType == PortsmouthBaseLayerTypes.PortsmouthAerial) {
            return this.GetPortsmouthAerial();
        }

        if (baseLayerType == PortsmouthBaseLayerTypes.PortsmouthOSSixInch) {
            return this.GetPortsmouthOSSixInch_1830_1880();
        }

        if (baseLayerType == PortsmouthBaseLayerTypes.PortsmouthOS25inch) {
            return this.GetPortsmouthOS25inch_1892_1914();
        }

        if (baseLayerType == PortsmouthBaseLayerTypes.PortsmouthOS1250) {
            return this.GetPortsmouthOS1250_1948_1975();
        }

        return super.GetBaseLayerByType(baseLayerType, bingApiKey, esdmOsmApiKey);
    }

    private GetPortsmouthOS(): IBaseLayer {

        const resolutions = [
            1222.9924523698928,
            611.4962261850814,
            305.7481130924087,
            152.87405654633633,
            76.43702827303586,
            38.21851413651793,
            19.109257068258966,
            9.554628534129483,
            4.777314267197037,
            2.3886571335985134,
            1.1943285666669667,
            0.5971642834657753,
            0.2985821416005962,
            0.1492910708002979
        ];

        const matrixIds = [
            'EPSG:3857:7', 'EPSG:3857:8', 'EPSG:3857:9', 'EPSG:3857:10',
            'EPSG:3857:11', 'EPSG:3857:12', 'EPSG:3857:13', 'EPSG:3857:14',
            'EPSG:3857:15', 'EPSG:3857:16', 'EPSG:3857:17', 'EPSG:3857:18',
            'EPSG:3857:19', 'EPSG:3857:20'
        ];

        const tileGrid = new WMTSTileGrid({
            origin: [-20037508.342787, 20037508.342787],
            resolutions: resolutions,
            matrixIds: matrixIds
        });

        let lyr = new TileLayer({
            extent: olProj.transformExtent([-7.68, 49.8, 1.77, 60.4], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            visible: true,
            source: new WMTS({
                url: "/api/OGCService/PortsmouthOS",
                layer: 'Road_3857',
                matrixSet: 'EPSG:3857',
                format: 'image/png',
                projection: 'EPSG:3857',
                style: 'default',
                tileGrid: tileGrid,
                crossOrigin: "Anonymous",
                attributions: [`© Crown copyright and database rights ${new Date().getFullYear()} OS AC0000807433. <br />Use of this data is subject to <a href="/terms-and-conditions#os">terms and conditions</a>.`]
            })
        });
        return new BaseLayer("PortsmouthOS", "Ordnance Survey map", lyr);
    }

    //Not yet configured, awaiting details from Portsmouth City Council
    private GetPortsmouthAerial(): IBaseLayer {
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                attributions: [`Aerial Photography 2016 © Getmapping.com`],
                url: "/api/ogcservice/PortsmouthAerial/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png",
                layer: "AERIAL_Aerial_2016",
                matrixSet: "default028mm",
                format: "image/png",
                version: "1.0.0",
                projection: "EPSG:27700",
                requestEncoding: "REST",
                tileGrid: new WMTSTileGrid({
                    origin: [-5220400, 4470200],
                    resolutions: [169.33367200065604, 84.66683600032802, 42.33341800016401, 21.166709000082005, 10.583354500041002, 5.291677250020501,
                        2.6458386250102506, 1.3229193125051253, 0.6614596562525626, 0.3307298281262813, 0.1984378968757688],
                    matrixIds: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]
                }),
                style: "default",
                wrapX: false,
                crossOrigin: "anonymous"
            })
        })

        return new BaseLayer("PortsmouthAerial", "Aerial", lyr);
    }

    protected GetPortsmouthOSSixInch_1830_1880(): IBaseLayer {
        const lyr = new TileLayer({
            extent: olProj.transformExtent([-7.68, 49.8, 1.77, 60.4], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            source: new XYZ({
                urls: [
                    'https://mapseries-tilesets.s3.amazonaws.com/os/six-inch-hampshireV/{z}/{x}/{y}.png'
                ],
                minZoom: 1,
                maxZoom: 18,
                attributions: 'Mapping from <a href="https://www.nls.uk/">National Library for Scotland</a>',
            }),
            visible: false,
        });
        lyr.set("name", "OSSixInch_1830_1880", true);
        return new BaseLayer("PortsmouthOSSixInch", "OS Six Inch to the mile (1830s-1880s)", lyr);
    }

    protected GetPortsmouthOS25inch_1892_1914(): IBaseLayer {
        const lyr = new TileLayer({
            extent: olProj.transformExtent([-7.68, 49.8, 1.77, 60.4], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            source: new XYZ({
                urls: [
                    'https://mapseries-tilesets.s3.amazonaws.com/25_inch/hampshire/{z}/{x}/{y}.png'
                ],
                minZoom: 1,
                maxZoom: 18,
                attributions: 'Mapping from <a href="https://www.nls.uk/">National Library for Scotland</a>',
            }),
            visible: false,
        });
        lyr.set("name", "OS25inch_1892_1914", true);
        return new BaseLayer("PortsmouthOS25inch", "OS 25 inch to the mile (1892-1914)", lyr);
    }

    protected GetPortsmouthOS1250_1948_1975(): IBaseLayer {
        const lyr = new TileLayer({
            extent: olProj.transformExtent([-7.68, 49.8, 1.77, 60.4], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            source: new XYZ({
                urls: [
                    'https://geo.nls.uk/maps/os/1250_B_1eng/{z}/{x}/{y}.png'
                ],
                minZoom: 1,
                maxZoom: 18,
                attributions: 'Mapping from <a href="https://www.nls.uk/">National Library for Scotland</a>',
            }),
            visible: false,
        });
        lyr.set("name", "OS1250_1948_1975", true);
        return new BaseLayer("PortsmouthOS1250", "OS 1:1250 (1948-1975)", lyr);
    }
}
